import { useCallback } from "react";

import { useRouter } from "next/router";
import { buildCreateReceiptAppliedOffer } from "../../utils/cart/appliedOffer";
import { handleConfirmReceipt } from "../../utils/RecieptAndCurrentOrderHelper";
import { handleCreateReceipt } from "../../utils/CreateReceiptHelper";
import { OrderTypesEnum, PAYMENT_METHOD, PagesEnum } from "../../utils/enums";
import { isVisitor } from "../../utils/help/user/user";
import {
  selectCartItems,
  selectCartTotal,
  selectIsEligibleOffer,
} from "../redux/selectors/cartSelectors";
import { selectLoyaltyCartResult } from "../redux/selectors/loyaltyCartSelectors";
import { selectRedeemedLoyaltyOffer } from "../redux/selectors/loyaltyOfferSelectors";
import { selectUseLoyaltyService } from "../redux/selectors/loyaltyServiceSelectors";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";
import {
  selectCurrentDeliveryConfig,
  selectDeliveryFees,
} from "../redux/selectors/deliverySelectors";

export const usePayHook = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const tableNumber = useAppSelector((state) => state.userInfo.tableNumber);
  const onlinePaymentProvider = useAppSelector(
    (state) => state.config.onlinePaymentProvider
  );

  const { franchise } = router.query;

  const cartAndLoyalty = useAppSelector((state) => state.cartAndLoyaltyReducer);
  const cartLoyaltyV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);
  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);
  const cartItems = useAppSelector(selectCartItems);
  const cartTotal = useAppSelector(selectCartTotal);
  const isEligibleOffer = useAppSelector(selectIsEligibleOffer);
  const loyaltyResult = useAppSelector(selectLoyaltyCartResult);
  const redeemedLoyaltyOffer = useAppSelector(selectRedeemedLoyaltyOffer);
  const token = useAppSelector((state) => state.userInfo.token);
  const deliveryAddress = useAppSelector((state) => state.delivery);
  const orderTypeParamState = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const referenceName = useAppSelector(
    (state) => state.reference.referenceName
  );
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);

  const { selfService } = useAppSelector((state) => state.franchiseStatus);

  const hasRoundingMethodAndLevel =
    !!restaurantInfo?.currencyRoundingMethod &&
    !!restaurantInfo?.currencyRoundingLevel;

  const scheduledPickup = useAppSelector(
    (state) => state.cartAndLoyaltyReducer.selectedPickupTime
  );

  const currentDeliveryConfig = useAppSelector(selectCurrentDeliveryConfig);
  const deliveryFees = useAppSelector(selectDeliveryFees);

  const pay = useCallback(
    async (
      pageURL: string,
      paymentMethod: PAYMENT_METHOD,
      tips?: number,
      savedCard?: any,
      /**
       * Pay with the stored wallet balance first, and/or with DSQ partner
       * points (loyalty checkout only).
       */
      options?: { useWallet?: boolean; payWithPartnerPoints?: boolean }
    ) => {
      const orderType =
        (orderTypeParamState as OrderTypesEnum) || OrderTypesEnum.TAKE_OUT;

      // loyaltyV2 path — same gate the cart-calculate flow uses.
      if (useLoyaltyService) {
        await handleCreateReceipt({
          pageURL,
          paymentMethod,
          tips,
          savedCard,
          dispatch,
          router,
          orderType,
          currentDeliveryConfig,
          deliveryAddress,
          franchise: franchise as string,
          referenceName,
          restaurantInfo,
          onlinePaymentProvider,
          tableNumber,
          hasRoundingMethodAndLevel,
          isVisitor: isVisitor(),
          redirectPage: PagesEnum.Home,
          cartItems,
          loyaltyResult,
          appliedOffer: buildCreateReceiptAppliedOffer(
            cartLoyaltyV2.loyalty.redemption,
            cartLoyaltyV2.loyalty.lines
          ),
          receiptNote: cartAndLoyalty.cart.receiptNote,
          scheduledSlotId:
            cartAndLoyalty.selectedScheduledOptions?.selectedSlot?.id,
          numberOfGuests:
            orderType === OrderTypesEnum.DINE_IN
              ? cartAndLoyalty.selectedScheduledOptions?.numberOfGuests
              : undefined,
          scheduledPickup,
          selfService,
          useWallet: options?.useWallet,
          payWithPartnerPoints: options?.payWithPartnerPoints,
        });

        return;
      }

      await handleConfirmReceipt({
        pageURL,
        paymentMethod,
        tips,
        savedCard,
        dispatch,
        router,
        orderType,
        cartAndLoyalty,
        currentDeliveryConfig,
        deliveryFees,
        deliveryAddress,
        franchise: franchise as string,
        referenceName,
        restaurantInfo,
        onlinePaymentProvider,
        tableNumber,
        hasRoundingMethodAndLevel,
        isVisitor: isVisitor(),
        redirectPage: PagesEnum.Home,
        cartLoyaltyV2: cartLoyaltyV2,
        cartItems,
        cartTotal,
        isEligibleOffer,
        redeemedOfferId: useLoyaltyService
          ? redeemedLoyaltyOffer?.id
          : undefined,
        selfService,
        scheduledPickup,
      });
    },
    [
      dispatch,
      router,
      orderTypeParamState,
      cartAndLoyalty,
      cartLoyaltyV2,
      cartItems,
      cartTotal,
      isEligibleOffer,
      loyaltyResult,
      useLoyaltyService,
      redeemedLoyaltyOffer?.id,
      currentDeliveryConfig,
      deliveryFees,
      deliveryAddress,
      franchise,
      referenceName,
      restaurantInfo,
      onlinePaymentProvider,
      tableNumber,
      hasRoundingMethodAndLevel,
      token,
      selfService,
      scheduledPickup,
    ]
  );

  return pay;
};
